import { BreakpointObserver } from '@angular/cdk/layout';
import { NgStyle } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, type QueryList, ViewChildren, computed, inject, input, type OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';

import type { Content, InvisualFilter } from '@23degrees/shared-code/models';
import { cloneObject } from '@23degrees/shared-code/utilities';
import { momentToStr, objToMoment } from '@23degrees/shared-code/utilities/date';
import { MessageBus } from '@23degrees/shared/bus';
import { EventBus } from '@23degrees/shared/bus/event/event.bus';
import { BaseClass } from '@23degrees/shared/classes/base.class';
import { BreakPointManager } from '@23degrees/shared/manager/breakpoint/breakpoint.manager';
import { ResponsiveStylePipe } from '@23degrees/shared/pipes';
import { ListSelectorComponent } from '@23degrees/ui/molecules/list-selector';
import { MenuButtonComponent } from '@23degrees/ui/molecules/menu-button';
import { TimelineControlComponent } from '@23degrees/ui/molecules/timeline-control';
import { ItemsSliderComponent } from '@23degrees/ui/organisms/items-slider/items-slider.component';

import { EDITOR_MESSAGES, MESSAGE_RECIPIENTS, REQUEST_MESSAGES } from '@config';

import type { ISmartConfig } from '..';

@Component({
  selector: 'content-filter',
  templateUrl: './content-filter.component.html',
  styleUrls: ['./content-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgStyle, FormsModule, TimelineControlComponent, MenuButtonComponent, ItemsSliderComponent, ListSelectorComponent, ResponsiveStylePipe],
})
export class ContentFilterComponent extends BaseClass implements OnInit {
  private eventBus = inject(EventBus);
  private messageBus = inject(MessageBus);
  private cdr = inject(ChangeDetectorRef);
  private breakPointManager = inject(BreakPointManager);
  private breakpointObserver = inject(BreakpointObserver);
  private responsiveStylePipe = inject(ResponsiveStylePipe);

  @ViewChildren('dropdownMenuButtons') dropdownMenuButtons: QueryList<MenuButtonComponent>;

  // TODO: signalize

  public config = input.required<ISmartConfig>();
  public content = input.required<Content>();
  public editor = input<boolean>(false);

  public activeFilter: string;
  public closeSelect = false;

  public baseOriginPosition = ['start', 'bottom'];
  public baseOverlayPosition = ['start', 'top'];

  public popupPositions = {};
  private openFirstDropdown = false;

  public inVisualFilter = computed<InvisualFilter[]>(() => {
    // rerender when config changes
    const config = this.config();
    // the values themselves are too -> but they are dependant on already selected values
    const inVisualFilter = this.content().typeSpecific.inVisualFilter;
    let activeFilter = inVisualFilter.filter((obj) => !obj.options.hide && obj.localStorage?.filterValues.length > 1);

    // check if there are any url parameter filters -> ?filter=
    const { filterVisual } = this.content().localStorage;
    if (filterVisual) {
      activeFilter = activeFilter.filter((obj) => obj.fieldName !== filterVisual.fieldName);
    }

    for (const filter of activeFilter) {
      this.setupLocalStorage(filter);
    }

    return activeFilter;
  });

  public displayScale = computed(() => this.config().displayScale ?? 1);

  public dropDownStyle = computed(() => this.content().typeSpecific.style.inVisualFilter.dropdown);

  public style = computed<CSSStyleDeclaration>(() => {
    const style = this.content().typeSpecific.style.inVisualFilter;
    const margin = this.responsiveStylePipe.transform(style.font.margin, 'spacing-horizontal');

    return {
      ...this.responsiveStylePipe.transform(style.font),
      maxWidth: `calc(100% - ${margin.left} - ${margin.right})`,
      borderRadius: `${style.borderRadius}px`,
      ...style.background,
    };
  });

  public openStyle = computed<CSSStyleDeclaration>(() => {
    if (!this.dropDownStyle().container.applyStyleToSourceButton) return this.style();
    const inVisualFilterStyle = this.content().typeSpecific.style.inVisualFilter;
    const style = {
      font: cloneObject(inVisualFilterStyle.font),
      background: this.dropDownStyle().container.background,
    };

    style.font.color = this.dropDownStyle().items.font.color;

    const margin = this.responsiveStylePipe.transform(inVisualFilterStyle.font.margin, 'spacing-horizontal');

    return {
      ...this.responsiveStylePipe.transform(style.font),
      maxWidth: `calc(100% - ${margin.left} - ${margin.right})`,
      borderRadius: `${inVisualFilterStyle.borderRadius}px`,
      ...style.background,
    };
  });

  filterContainerStyle = (filter: InvisualFilter) => {
    const baseStyle = this.activeFilter === filter.localStorage.title ? this.openStyle() : this.style();
    // Extra css in case buttons (inline or not)
    if (filter.options.appearance === 'buttons') {
      const custom = filter.options.buttonsStyle;
      const global = this.content().typeSpecific.style.inVisualFilter.buttons;
      const grow = custom.grow ?? global.grow;
      baseStyle.display = 'flex';
      baseStyle.flexGrow = grow ? '1' : '0';
    }

    return baseStyle;
  };

  ngOnInit() {
    const { referenceID } = this.content().localStorage;

    this.messageBus.on(EDITOR_MESSAGES.InvisUpdate, MESSAGE_RECIPIENTS.InvisualFilter, this.repositionDropDowns, this.componentDestroyed$, referenceID);
    this.messageBus.on(EDITOR_MESSAGES.InvisDropdownEditingActive, MESSAGE_RECIPIENTS.InvisualFilter, this.openDropdown, this.componentDestroyed$, referenceID);
    this.messageBus.on('RENDER_FINISH', MESSAGE_RECIPIENTS.Filter, this.playButtonFinish, this.componentDestroyed$, referenceID);
  }

  public repositionDropDowns = () => {
    if (this.dropdownMenuButtons && this.dropdownMenuButtons.length > 0) {
      const openDropDowns = this.dropdownMenuButtons.toArray().filter((dropdown) => dropdown.open);

      for (const dropDown of openDropDowns) {
        dropDown.reposition();
      }
    }
  };

  public openDropdown = (msg) => {
    if (this.breakpointObserver.isMatched('(max-width: 900px)')) return;

    this.openFirstDropdown = msg;
    if (this.dropdownMenuButtons && this.dropdownMenuButtons.length > 0) {
      const dropDown = this.dropdownMenuButtons.first;

      if (this.openFirstDropdown) {
        dropDown.preventClosing = true;
        dropDown.autoclose = false;

        if (!dropDown.open) {
          dropDown.openMenu(true);
        }
      } else {
        dropDown.preventClosing = false;
        dropDown.closeAction();
        dropDown.autoclose = true;
      }
    }
  };

  private setupLocalStorage = (filter: InvisualFilter) => {
    const filterField = this.content().typeSpecific.fields[filter.fieldName];
    const { format, dataType, title } = filterField;

    filter.localStorage.title = title;
    filter.localStorage.type = dataType;
    filter.localStorage.filterObjects = filter.localStorage.filterValues.map((obj) => {
      let isSelected = filter.selectedValue === obj;
      if (filter.type === 'multiValueSelector') {
        isSelected = filter.selectedValues.includes(obj) || filter.selectedValues.length === 0;
      }

      if (dataType === 'date') {
        const date = objToMoment(obj, format.date.inputFormat, format.locale);
        return {
          label: momentToStr(date, format.date.dateFormat, format.locale),
          value: obj,
          date: momentToStr(date, format.date.dateFormat, format.locale), // dateObjToBaseDateObj(date),
          dateValue: date.valueOf(),
          selected: isSelected,
        };
      }

      return {
        label: obj,
        value: obj,
        date: obj,
        selected: isSelected,
      };
    });

    if (filter.type === 'multiValueSelector') {
      filter.localStorage.selectedValues = cloneObject(filter.selectedValues);
      if (filter.localStorage.selectedValues.length === 0) {
        filter.localStorage.selectedValues = cloneObject(filter.localStorage.filterValues);
      }
    }

    // play button
    if (filter.type === 'valueSelector') {
      // always setup playButton, localStorage got serialized and the signal is lost
      // filter.localStorage.playButton = { << @Gregor: this breaks the play button
      filter.localStorage.playButton = filter.localStorage.playButton || {
        active: filter.options.playButton.autoStart,
        timerRef: undefined,
      };
    }
  };

  public formatSelectedValue = (filter: InvisualFilter, value) => {
    const filterField = this.content().typeSpecific.fields[filter.fieldName];
    if (!filterField) return value;

    const { format, dataType, title } = filterField;
    if (dataType === 'date') {
      const date = objToMoment(value, format.date.inputFormat, format.locale);
      return momentToStr(date, format.date.dateFormat, format.locale);
    }

    return value;
  };

  public updateFilter = (filter: InvisualFilter, stopAnimation = true) => {
    if (filter.localStorage?.playButton?.active) {
      filter.localStorage.playButton.active = !stopAnimation;
    }

    this.eventBus.filterChange.next({ slug: this.content().slug, context: { filter } });

    const { referenceID } = this.content().localStorage;
    this.messageBus.emit(
      REQUEST_MESSAGES.FilterUpdate,
      [MESSAGE_RECIPIENTS.Chart, MESSAGE_RECIPIENTS.Map],
      { content: this.content(), dataId: this.content().typeSpecific.dataId },
      referenceID,
    );
  };

  opened(isOpen: boolean, filter: string) {
    if (isOpen) {
      this.activeFilter = filter;
    } else if (this.activeFilter === filter) {
      this.activeFilter = null;
    }
  }

  singleOutElement(filter: InvisualFilter, item) {
    filter.selectedValues = [item.value];
    this.updateFilter(filter);
  }

  setValues(filter: InvisualFilter, data) {
    if (filter.type === 'valueSelector') {
      filter.selectedValue = data;
      this.closeSelect = !this.closeSelect;
    } else {
      // check if we have zero
      if (data.length === 0) {
        filter.selectedValues = [];
        filter.localStorage.selectedValues = cloneObject(filter.localStorage.filterValues);
      } else {
        filter.selectedValues = data;
        filter.localStorage.selectedValues = data;
      }
    }

    this.updateFilter(filter);
  }

  trackByFunction = (item) => {
    return item.value;
  };

  private playButtonFinish = () => {
    for (const filter of this.inVisualFilter()) {
      if (filter.type !== 'valueSelector') continue;
      const { localStorage, options } = filter;

      if (options.playButton.enabled && localStorage.playButton.active && !localStorage.playButton.timerRef) {
        localStorage.playButton.timerRef = setTimeout(this.playButtonInterval, options.playButton.interval, filter);
        // console.log('create timeout', options.playButton.interval, localStorage.playButton.timerRef);
      }
    }
  };

  public playButtonClick = (filter: InvisualFilter) => {
    const { localStorage, options } = filter;
    localStorage.playButton.active = !localStorage.playButton.active;

    if (localStorage.playButton.timerRef) {
      // console.log('click > clear timer', localStorage.playButton.timerRef);
      clearTimeout(localStorage.playButton.timerRef);
      localStorage.playButton.timerRef = undefined;
    }

    if (localStorage.playButton.active) {
      this.playButtonInterval(filter, true);
    }
  };

  private playButtonInterval = (filter: InvisualFilter, overrideLoop = false) => {
    const { localStorage, selectedValue, options } = filter;
    const { filterValues } = localStorage;
    const shouldLoop = overrideLoop || options.playButton.loop;

    if (localStorage.playButton.timerRef) {
      console.log('interval > clear timer', localStorage.playButton.timerRef);
      clearTimeout(localStorage.playButton.timerRef);
      localStorage.playButton.timerRef = undefined;
    }

    // stop unless override or loop is enabled
    if (selectedValue === filterValues[filterValues.length - 1] && !shouldLoop) {
      localStorage.playButton.active = false;
      this.cdr.detectChanges();
      return;
    }

    const curIdx = filterValues.indexOf(selectedValue);
    const nextIdx = (curIdx + 1) % filterValues.length;

    filter.selectedValue = filterValues[nextIdx];
    this.updateFilter(filter, false);
  };

  public setPopupPositions(filter, positions) {
    this.popupPositions[filter] = positions;
  }

  public getPopupPosition(filter) {
    return this.popupPositions[filter] || { overlayPosition: this.baseOverlayPosition, originPosition: this.baseOriginPosition };
  }

  public getFilterTitle(filter: InvisualFilter) {
    if (filter.options.titleEnabled) {
      const { enabled, prefix, suffix } = filter.options.showCharacteristic;
      const charStr = prefix + this.formatSelectedValue(filter, filter.selectedValue) + suffix;
      return filter.options.title + (enabled ? charStr : '');
    }

    return this.formatSelectedValue(filter, filter.selectedValue);
  }

  public getButtonStyle = (idx: number, active: boolean) => {
    const custom = this.inVisualFilter()[idx].options.buttonsStyle;
    const global = this.content().typeSpecific.style.inVisualFilter.buttons;
    const border = custom.border ?? global.border;
    const { size } = border;
    const padding = custom.padding ?? global.padding;
    const color = custom.color ?? global.color;
    const activeColor = custom.activeColor ?? global.activeColor;
    const background = custom.background ?? global.background;
    const activeBackground = custom.activeBackground ?? global.activeBackground;
    const position = custom.position ?? global.position;
    const style: any = {
      paddingTop: `${padding.top}${padding.topUnit}`,
      paddingBottom: `${padding.bottom}${padding.bottomUnit}`,
      paddingLeft: `${padding.left}${padding.leftUnit}`,
      paddingRight: `${padding.right}${padding.rightUnit}`,
      borderRadius: `${custom.borderRadius ?? global.borderRadius}px`,
      color: active ? activeColor : color,
      backgroundColor: active ? activeBackground : background,
      borderColor: border.color,
      borderStyle: border.type,
      borderWidth: `${size.top}px ${size.right}px ${size.bottom}px ${size.left}px`,
    };

    if (position === 'stretch') {
      style.display = 'flex';
      style.flexGrow = '1';
      style.justifyContent = 'center';
    }

    return style;
  };

  public getButtonsGroupStyle = (idx: number) => {
    const custom = this.inVisualFilter()[idx].options.buttonsStyle;
    const global = this.content().typeSpecific.style.inVisualFilter.buttons;
    const style = {
      display: 'flex',
      gap: `${custom.gap ?? global.gap}px`,
      width: '100%',
      justifyContent: custom.position ?? global.position,
      flexWrap: (custom.wrap ?? global.wrap) ? 'wrap' : 'nowrap',
    };

    return style;
  };
}
